import {
  createFrontendPlugin,
  createRouteRef,
  PageBlueprint,
} from '@backstage/frontend-plugin-api';
import {
  EntityCardBlueprint,
  EntityContentBlueprint,
} from '@backstage/plugin-catalog-react/alpha';
import GavelIcon from '@material-ui/icons/Gavel';

/**
 * Open source licenses in the portal: what each service depends on and under
 * what terms, read out of the CycloneDX SBOMs the repos already publish to
 * S3 (see the backend's oss-licenses plugin).
 *
 * Everything here is read-only. The SBOM is generated in CI from the lock
 * files, so the portal reports licenses and never becomes a second place
 * where one can be declared.
 */

/**
 * Which kinds get the per-entity tab.
 *
 * Components only: one repo, one SBOM, one exact entity ref to resolve.
 * Systems get their own extension below rather than joining this list,
 * because they have no repo of their own and have to aggregate instead.
 */
const LICENSE_KINDS = ['component'];

/**
 * A page only reaches the sidebar if it carries a route ref: AppNav builds
 * the nav from the app's route nodes and drops any whose `core.routing.ref`
 * is absent. Same reason as the BCM module's processes page.
 */
const licensesRouteRef = createRouteRef();

/** /licenses - the company-wide inventory, for risk and legal. */
export const licensesPage = PageBlueprint.make({
  params: {
    path: '/licenses',
    routeRef: licensesRouteRef,
    icon: <GavelIcon fontSize="inherit" />,
    title: 'Open source licenses',
    loader: async () => import('./LicensesPage').then(m => <m.LicensesPage />),
  },
});

/**
 * /licenses/corrections - the corrections register.
 *
 * Without a route ref, so it does not take a sidebar slot beside the
 * inventory: it is evidence for the inventory rather than a destination.
 */
export const licenseCorrectionsPage = PageBlueprint.make({
  name: 'corrections',
  params: {
    path: '/licenses/corrections',
    loader: async () =>
      import('./LicenseCorrectionsPage').then(m => (
        <m.LicenseCorrectionsPage />
      )),
  },
});

/**
 * /licenses/policy - the policy itself, rendered from license-policy.yaml.
 *
 * Deliberately without a route ref, so it does not take a second slot in the
 * sidebar beside the inventory. It is context for the inventory rather than
 * a separate destination, and the ticket asks for it to be one click from a
 * license view rather than a nav entry of its own. Adding a ref later is a
 * one-line change if Legal would rather reach it directly.
 */
export const licensePolicyPage = PageBlueprint.make({
  name: 'policy',
  params: {
    path: '/licenses/policy',
    loader: async () =>
      import('./LicensePolicyPage').then(m => <m.LicensePolicyPage />),
  },
});

/** The per-service dependency list, with its license and category. */
const licensesEntityContent = EntityContentBlueprint.make({
  params: {
    path: 'licenses',
    title: 'Licenses',
    filter: entity => LICENSE_KINDS.includes(entity.kind.toLowerCase()),
    loader: async () =>
      import('./LicensesContent').then(m => <m.LicensesContent />),
  },
});

/**
 * The same tab on a System, rolling up the Components beneath it.
 *
 * Kept separate from the Component tab rather than widening its filter,
 * because the two answer differently shaped questions. A Component has one
 * repo and therefore one SBOM; a System has none of its own and is an
 * aggregation over members that publish independently, so its view has to
 * lead with how much of itself it is actually covering.
 */
const systemLicensesContent = EntityContentBlueprint.make({
  name: 'system-licenses',
  params: {
    path: 'licenses',
    title: 'Licenses',
    filter: entity => entity.kind.toLowerCase() === 'system',
    loader: async () =>
      import('./SystemLicensesContent').then(m => <m.SystemLicensesContent />),
  },
});

/** One-glance version on the entity Overview: how much needs attention. */
const licensesCard = EntityCardBlueprint.make({
  name: 'licenses',
  params: {
    type: 'info',
    filter: entity => LICENSE_KINDS.includes(entity.kind.toLowerCase()),
    loader: async () => import('./LicensesCard').then(m => <m.LicensesCard />),
  },
});

/**
 * The same at-a-glance view on a team's page, across every service it owns.
 *
 * Its own card rather than the Component card with a wider filter: a team
 * has no repo and no SBOM, so it answers from a rollup, and it has to say
 * how much of the team that rollup covers before anything else.
 */
const teamLicensesCard = EntityCardBlueprint.make({
  name: 'team-licenses',
  params: {
    type: 'info',
    filter: entity => entity.kind.toLowerCase() === 'group',
    loader: async () =>
      import('./TeamLicensesCard').then(m => <m.TeamLicensesCard />),
  },
});

export const ossLicensesPlugin = createFrontendPlugin({
  pluginId: 'oss-licenses',
  routes: { licenses: licensesRouteRef },
  extensions: [
    licensesPage,
    licenseCorrectionsPage,
    licensePolicyPage,
    licensesEntityContent,
    systemLicensesContent,
    licensesCard,
    teamLicensesCard,
  ],
});
